import * as React from "react";
import PropTypes from "prop-types";
import Card from "@mui/material/Card";
import CardActions from "@mui/material/CardActions";
import CardContent from "@mui/material/CardContent";
import Typography from "@mui/material/Typography";
import {
  CardHeader,
  Chip,
  IconButton,
  Tooltip,
  ClickAwayListener,
  Box,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import { Icon } from "@iconify/react";
import { useDispatch } from "react-redux";
import { setDialog } from "../../../features/dialog/dialogSlice";
import useAuth from "../../../hooks/useAuth";
import usePlanGate from "../../../hooks/usePlanGate";
import FeatureLockedDialog from "../../../components/dialog/FeatureLockedDialog";
import { FEATURE_KEYS } from "../../../utils/planFeatures";
import {
  getOpeningDayLabel,
  normalizeDayValues,
  sortOpeningHoursByDay,
  sortTrainingSessions,
} from "../../../utils/openingHours";

const MEMBER_VISIBILITY_ITEMS = [
  {
    icon: "mdi:map-marker-outline",
    label: "Standortname sowie Anschrift (Straße, PLZ, Ort)",
  },
  {
    icon: "mdi:email-outline",
    label: "Kontaktangaben (E-Mail, Rufnummer, Homepage), sofern hinterlegt",
  },
  {
    icon: "mdi:clock-time-four-outline",
    label: "Öffnungszeiten (oder alternativ öffentliche Öffnungszeiten)",
  },
  {
    icon: "mdi:calendar-clock-outline",
    label: "Trainingsplan (oder alternativ öffentliche Trainingsangaben)",
  },
  {
    icon: "mdi:information-outline",
    label: 'Text aus "Über den Standort"',
  },
  {
    icon: "mdi:account-plus-outline",
    label: "Optional: Link zur öffentlichen Anmeldung (tarifabhängig)",
  },
];

export default function SiteCardItem({ site }) {
  const dispatch = useDispatch();
  const { isAdmin } = useAuth();
  const {
    isLoading: isPlanLoading,
    isAllowed: canUsePublicRegistration,
    isDialogOpen: isFeatureDialogOpen,
    requiredPlanLabel,
    openDialog: openFeatureDialog,
    closeDialog: closeFeatureDialog,
    goToUpgrade: goToFeatureUpgrade,
  } = usePlanGate(FEATURE_KEYS.PUBLIC_REGISTRATION);
  const [menuAnchor, setMenuAnchor] = React.useState(null);
  const [isVisibilityHintPinned, setIsVisibilityHintPinned] =
    React.useState(false);
  const [isVisibilityHintHovered, setIsVisibilityHintHovered] =
    React.useState(false);
  const menuOpen = Boolean(menuAnchor);
  const memberVisibilityHintOpen =
    isVisibilityHintPinned || isVisibilityHintHovered;

  const openMenu = (event) => {
    setMenuAnchor(event.currentTarget);
  };

  const closeMenu = () => {
    setMenuAnchor(null);
  };

  const closeMemberVisibilityHint = () => {
    setIsVisibilityHintPinned(false);
    setIsVisibilityHintHovered(false);
  };

  const toggleMemberVisibilityHint = (event) => {
    event.stopPropagation();
    setIsVisibilityHintPinned((prev) => {
      const next = !prev;
      if (!next) {
        setIsVisibilityHintHovered(false);
      }
      return next;
    });
  };

  const handleMenuAction = (fn) => {
    closeMenu();
    fn();
  };
  const openOverview = () => {
    dispatch(
      setDialog({
        title: "Standortübersicht",
        content: "site-overview",
        type: "SHOW",
        data: site,
      }),
    );
  };
  const openingHours = React.useMemo(
    () => sortOpeningHoursByDay(site?.openingHours || []),
    [site?.openingHours],
  );
  const trainingSessions = React.useMemo(
    () => sortTrainingSessions(site?.trainingSessions || []),
    [site?.trainingSessions],
  );

  const openDelete = () => {
    dispatch(
      setDialog({ title: "Standort löschen", type: "DELETE", data: site }),
    );
  };

  const openEdit = () => {
    dispatch(
      setDialog({ title: "Standort bearbeiten", type: "EDIT", data: site }),
    );
  };

  // URL mit OG-Meta-Tags für WhatsApp/Social-Media-Previews (server-side redirect)
  const getShareUrl = () =>
    `${new URL(window.location.href).origin}/registration/${site?._id}`;

  const openRegistrationQR = () => {
    if (isPlanLoading) return;
    if (!canUsePublicRegistration) {
      openFeatureDialog();
      return;
    }
    dispatch(
      setDialog({
        title: "QR Code für Anmeldung",
        content: "QR",
        type: "SHOW",
        value: getShareUrl(),
      }),
    );
  };

  const copyRegistrationLink = () => {
    if (isPlanLoading) return;
    if (!canUsePublicRegistration) {
      openFeatureDialog();
      return;
    }
    navigator.clipboard.writeText(getShareUrl());
  };

  const openRegistrationPage = () => {
    if (isPlanLoading) return;
    if (!canUsePublicRegistration) {
      openFeatureDialog();
      return;
    }
    let parsedURL = new URL(window.location.href);
    window.open(`${parsedURL.origin}/registration/${site?._id}`, "_blank");
  };

  return (
    <>
      <Card
        sx={{
          minWidth: 275,
          minHeight: 410,
          borderRadius: 3,
          border: "1px solid rgba(0,0,0,0.08)",
          boxShadow: "0 10px 30px rgba(0,0,0,0.08)",
          background:
            "linear-gradient(135deg, rgba(255,255,255,1) 0%, rgba(248,248,248,1) 100%)",
          transition: "transform 180ms ease, box-shadow 180ms ease",
          "&:hover": {
            transform: "translateY(-4px)",
            boxShadow: "0 14px 34px rgba(0,0,0,0.12)",
          },
        }}
      >
        <CardHeader
          action={
            <>
              <IconButton aria-label="settings" onClick={openMenu}>
                <Icon icon="material-symbols:more-vert" />
              </IconButton>
              <Menu
                anchorEl={menuAnchor}
                open={menuOpen}
                onClose={closeMenu}
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                transformOrigin={{ vertical: "top", horizontal: "right" }}
              >
                <MenuItem onClick={() => handleMenuAction(openOverview)}>
                  <ListItemIcon>
                    <Icon icon="mdi:file-pdf-box" />
                  </ListItemIcon>
                  <ListItemText>Standortübersicht (PDF)</ListItemText>
                </MenuItem>
                {isAdmin && (
                  <MenuItem onClick={() => handleMenuAction(openEdit)}>
                    <ListItemIcon>
                      <Icon icon="material-symbols:edit" />
                    </ListItemIcon>
                    <ListItemText>Standort bearbeiten</ListItemText>
                  </MenuItem>
                )}
                <MenuItem onClick={() => handleMenuAction(openRegistrationQR)}>
                  <ListItemIcon>
                    <Icon icon="mdi:qrcode" />
                  </ListItemIcon>
                  <ListItemText>QR Code anzeigen</ListItemText>
                </MenuItem>
                <MenuItem
                  onClick={() => handleMenuAction(copyRegistrationLink)}
                >
                  <ListItemIcon>
                    <Icon icon="mdi:content-copy" />
                  </ListItemIcon>
                  <ListItemText>Anmeldelink kopieren</ListItemText>
                </MenuItem>
                <MenuItem
                  onClick={() => handleMenuAction(openRegistrationPage)}
                >
                  <ListItemIcon>
                    <Icon icon="mdi:open-in-new" />
                  </ListItemIcon>
                  <ListItemText>Anmeldung öffnen</ListItemText>
                </MenuItem>
                {isAdmin && (
                  <MenuItem
                    onClick={() => handleMenuAction(openDelete)}
                    sx={{ color: "error.main" }}
                  >
                    <ListItemIcon sx={{ color: "error.main" }}>
                      <Icon icon="material-symbols:delete" />
                    </ListItemIcon>
                    <ListItemText>Standort löschen</ListItemText>
                  </MenuItem>
                )}
              </Menu>
            </>
          }
          title={`${site?.siteName}`}
          titleTypographyProps={{ variant: "h5", textAlign: "center" }}
          subheader={`Leitung: ${site?.siteOwner}`}
          subheaderTypographyProps={{ textAlign: "center" }}
          sx={{ borderBottom: "1px solid rgba(0,0,0,0.06)" }}
        />
        <CardContent>
          <Box
            sx={{ mb: 1.5, display: "flex", alignItems: "center", gap: 0.75 }}
          >
            <Chip
              size="small"
              color={site?.visibleForMembers === false ? "default" : "success"}
              label={
                site?.visibleForMembers === false
                  ? "Für Mitglieder ausgeblendet"
                  : "Für Mitglieder sichtbar"
              }
            />
            <ClickAwayListener onClickAway={closeMemberVisibilityHint}>
              <Box sx={{ display: "inline-flex" }}>
                <Tooltip
                  arrow
                  placement="top-start"
                  open={memberVisibilityHintOpen}
                  disableHoverListener
                  disableFocusListener
                  disableTouchListener
                  title={
                    <Box sx={{ py: 0.5, maxWidth: 360 }}>
                      <Typography
                        variant="subtitle2"
                        sx={{ color: "inherit", fontWeight: 700, mb: 0.75 }}
                      >
                        Sichtbar in der Mitgliederansicht
                      </Typography>
                      <Box sx={{ display: "grid", gap: 0.6 }}>
                        {MEMBER_VISIBILITY_ITEMS.map((entry) => (
                          <Box
                            key={entry.label}
                            sx={{
                              display: "flex",
                              gap: 0.7,
                              alignItems: "flex-start",
                            }}
                          >
                            <Icon
                              icon={entry.icon}
                              width={14}
                              style={{
                                color: "currentColor",
                                marginTop: 1,
                                flexShrink: 0,
                              }}
                            />
                            <Typography
                              variant="caption"
                              sx={{ color: "inherit", lineHeight: 1.35 }}
                            >
                              {entry.label}
                            </Typography>
                          </Box>
                        ))}
                      </Box>
                      <Typography
                        variant="caption"
                        sx={{
                          display: "block",
                          mt: 0.9,
                          color: "inherit",
                          opacity: 0.95,
                          lineHeight: 1.35,
                        }}
                      >
                        Nicht sichtbar: Mitgliederanzahl, Beiträge, interne
                        Vertrags- und Verwaltungsdaten.
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{
                          display: "block",
                          mt: 0.75,
                          color: "inherit",
                          opacity: 0.95,
                          lineHeight: 1.35,
                        }}
                      >
                        {site?.visibleForMembers === false
                          ? "Hinweis: Bei ausgeblendeten Standorten sehen nur zugewiesene Mitglieder ihren eigenen Standort."
                          : "Hinweis: Dieser Standort ist für Mitglieder in der allgemeinen Standortliste freigegeben."}
                      </Typography>
                    </Box>
                  }
                >
                  <IconButton
                    size="small"
                    aria-label="Informationen zur Mitgliederansicht"
                    aria-pressed={isVisibilityHintPinned}
                    onMouseEnter={() => setIsVisibilityHintHovered(true)}
                    onMouseLeave={() => setIsVisibilityHintHovered(false)}
                    onFocus={() => setIsVisibilityHintHovered(true)}
                    onBlur={() => setIsVisibilityHintHovered(false)}
                    onClick={toggleMemberVisibilityHint}
                    onKeyDown={(event) => {
                      if (event.key === "Escape") {
                        closeMemberVisibilityHint();
                      }
                    }}
                    sx={{
                      width: 24,
                      height: 24,
                      border: "1px solid",
                      borderColor: "divider",
                      color: "text.secondary",
                      backgroundColor: "rgba(255, 255, 255, 0.95)",
                      transition: "all 140ms ease",
                      "&:hover": {
                        color: "primary.main",
                        borderColor: "primary.main",
                        backgroundColor: "rgba(25, 118, 210, 0.08)",
                      },
                    }}
                  >
                    <Icon icon="mdi:help-circle-outline" width={16} />
                  </IconButton>
                </Tooltip>
              </Box>
            </ClickAwayListener>
          </Box>
          <Typography sx={{ mb: 1.5 }} variant="h6" component="div">
            Mitglieder: {site?.activeMembers}
          </Typography>
          <Typography sx={{ mb: 1.5 }} variant="h6" component="div">
            Mitgliedsbeitrag: {site?.memberContribution},00 €
          </Typography>
          <Typography color="text.secondary">{site?.street}</Typography>
          <Typography sx={{ mb: 1.5 }} color="text.secondary">
            {site?.zip + " " + site?.city}
          </Typography>
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2">Öffnungszeiten</Typography>
            {openingHours.length > 0 ? (
              openingHours.map((entry) => (
                <Typography key={`${entry.day}-${entry.start}`} variant="body2">
                  {getOpeningDayLabel(entry.day)}: {entry.start} - {entry.end}
                </Typography>
              ))
            ) : (
              <Typography variant="body2" color="text.secondary">
                Öffnungszeiten noch nicht hinterlegt.
              </Typography>
            )}
          </Box>
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2">Trainingsplan</Typography>
            {trainingSessions.length > 0 ? (
              trainingSessions.map((entry) => {
                const normalizedDays = normalizeDayValues(
                  entry?.days ?? entry?.day,
                );
                const dayLabel =
                  normalizedDays.length > 0
                    ? normalizedDays.map(getOpeningDayLabel).join(", ")
                    : getOpeningDayLabel(entry.day);
                return (
                  <Typography
                    key={`${dayLabel}-${entry.start}-${entry.title}`}
                    variant="body2"
                  >
                    {dayLabel} · {entry.title}: {entry.start} - {entry.end}
                  </Typography>
                );
              })
            ) : (
              <Typography variant="body2" color="text.secondary">
                Trainings noch nicht hinterlegt.
              </Typography>
            )}
          </Box>
        </CardContent>
        <CardActions
          style={{ justifyContent: "space-between" }}
          sx={{ borderTop: "1px solid rgba(0,0,0,0.06)" }}
          disableSpacing
        >
          <Tooltip
            title="Standortübersicht als PDF erstellen."
            arrow
            placement="top"
          >
            <IconButton onClick={openOverview} aria-label="Standortübersicht">
              <Icon icon="mdi:file-pdf-box" />
            </IconButton>
          </Tooltip>
          {isAdmin && (
            <Tooltip
              title="Öffnet den Standort zur Bearbeitung."
              arrow
              placement="top"
            >
              <IconButton onClick={openEdit} aria-label="Standort bearbeiten">
                <Icon icon="material-symbols:edit" />
              </IconButton>
            </Tooltip>
          )}
          <Tooltip
            title="Zeigt den QR-Code zur öffentlichen Anmeldung dieses Standorts."
            arrow
            placement="top"
          >
            <IconButton
              onClick={openRegistrationQR}
              aria-label="QR Code anzeigen"
            >
              <Icon icon="mdi:qrcode" />
            </IconButton>
          </Tooltip>
          <Tooltip
            title="Öffnet die öffentliche Anmeldeseite dieses Standorts in einem neuen Tab."
            arrow
            placement="top"
          >
            <IconButton
              onClick={openRegistrationPage}
              aria-label="Anmeldung öffnen"
            >
              <Icon icon="mdi:open-in-new" />
            </IconButton>
          </Tooltip>
          {isAdmin && (
            <Tooltip
              title="Löscht den Standort nach Bestätigung."
              arrow
              placement="top"
            >
              <IconButton
                onClick={() => openDelete()}
                aria-label="Standort löschen"
              >
                <Icon icon="material-symbols:delete" />
              </IconButton>
            </Tooltip>
          )}
        </CardActions>
      </Card>
      <FeatureLockedDialog
        open={isFeatureDialogOpen}
        onClose={closeFeatureDialog}
        onUpgrade={goToFeatureUpgrade}
        upgradePlanLabel={requiredPlanLabel}
      />
    </>
  );
}

SiteCardItem.propTypes = {
  site: PropTypes.object,
};
